import { useState } from "react";
import { Check, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/trpc/trpc";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 100;

function formatPrice(cents: number) {
  return `${(cents / 100).toFixed(2)} zł`;
}

export type PickedTreatment = { id: string; name: string; price: number };

type Props = {
  /** Ids currently chosen; rendered as selected. */
  selectedIds: string[];
  /** Called with the clicked treatment id and its basic data. */
  onPick: (id: string, treatment: PickedTreatment) => void;
  /**
   * `multi` toggles rows in and out of the selection; `single` treats every
   * click as "pick this one". Both just call `onPick` — the parent owns state.
   */
  mode?: "single" | "multi";
  /** Hide these ids from the list (e.g. treatments already added elsewhere). */
  excludeIds?: string[];
  /** Skip fetching while the hosting dialog is closed. */
  enabled?: boolean;
  /** Height of the scrollable result list. */
  listClassName?: string;
  emptyText?: string;
  id?: string;
};

/**
 * Searchable treatment list for every place in the panel where a treatment is
 * chosen. Search runs on the server, so salons with more treatments than one
 * page still reach every entry; tag chips narrow the result on the client.
 */
export function TreatmentPicker({
  selectedIds,
  onPick,
  mode = "multi",
  excludeIds,
  enabled = true,
  listClassName,
  emptyText = "Brak zabiegów w tym salonie",
  id,
}: Props) {
  const [search, setSearch] = useState("");
  const [tagId, setTagId] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(search.trim(), 250);

  // Both filters run on the server so salons with more treatments than one
  // page still reach every entry.
  const { data, isLoading, isFetching } = trpc.treatment.list.useQuery(
    {
      page: 1,
      limit: PAGE_SIZE,
      search: debouncedSearch || undefined,
      tagId: tagId ?? undefined,
    },
    { enabled, placeholderData: (prev) => prev },
  );
  const { data: tags } = trpc.tag.list.useQuery(undefined, { enabled });

  const excluded = new Set(excludeIds ?? []);
  const selected = new Set(selectedIds);
  const items = (data?.items ?? []).filter((t) => !excluded.has(t.id));
  const truncated = (data?.total ?? 0) > PAGE_SIZE;
  const isFiltering = Boolean(debouncedSearch) || Boolean(tagId);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id={id}
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Szukaj zabiegu po nazwie..."
          className="pl-8"
          autoComplete="off"
        />
      </div>

      {tags && tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <Badge
            variant={tagId === null ? "default" : "outline"}
            className="cursor-pointer"
            onClick={() => setTagId(null)}
          >
            Wszystkie
          </Badge>
          {tags.map((tag) => (
            <Badge
              key={tag.id}
              variant={tagId === tag.id ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => setTagId(tagId === tag.id ? null : tag.id)}
            >
              {tag.name}
            </Badge>
          ))}
        </div>
      )}

      <div
        className={cn(
          "rounded-md border divide-y overflow-y-auto",
          isFetching && "opacity-70",
          listClassName ?? "max-h-64",
        )}
        role="listbox"
        aria-multiselectable={mode === "multi"}
      >
        {isLoading ? (
          <p className="px-3 py-4 text-sm text-muted-foreground text-center">
            Ładowanie...
          </p>
        ) : items.length === 0 ? (
          <p className="px-3 py-4 text-sm text-muted-foreground text-center">
            {isFiltering ? "Brak zabiegów pasujących do filtra" : emptyText}
          </p>
        ) : (
          items.map((t) => {
            const isSelected = selected.has(t.id);
            return (
              <button
                key={t.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() =>
                  onPick(t.id, { id: t.id, name: t.name, price: t.price })
                }
                className={cn(
                  "flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent",
                  isSelected && "bg-accent/40",
                )}
              >
                <span
                  className={cn(
                    "flex size-4 shrink-0 items-center justify-center rounded border",
                    isSelected
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-input",
                  )}
                  aria-hidden
                >
                  {isSelected && <Check className="size-3" />}
                </span>
                <span className="flex-1 truncate">
                  {t.name}
                  {t.status !== "active" && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({t.status === "draft" ? "szkic" : "nieaktywny"})
                    </span>
                  )}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatPrice(t.price)}
                </span>
              </button>
            );
          })
        )}
      </div>

      {truncated && !isFiltering && (
        <p className="text-xs text-muted-foreground">
          Pokazano pierwsze {PAGE_SIZE} z {data?.total} zabiegów. Wpisz nazwę,
          aby znaleźć pozostałe.
        </p>
      )}
    </div>
  );
}
